import HelpIcon from '@mui/icons-material/Help';
import React, { FC, useEffect, useState } from 'react';
import { Button, Dropdown, BhiFormDialog, TextInput } from 'src/UIKit';
import { CodeBlock } from '../CodeBlock';
import { addIntegrationValidationSchema, ValidationErrors } from 'src/constants/validations';
import * as Yup from 'yup';
import { AddIntegrationModalController } from '../../controllers';
import { IntegrationFormData } from '../../types';

const emptyFormValues: IntegrationFormData = {
  name: '',
  eventType: 'new_inbox_message',
  url: '',
};

interface AddIntegrationModalProps extends AddIntegrationModalController {}

export const AddIntegrationModal: FC<AddIntegrationModalProps> = props => {
  const { isOpen, initFormData, hookEventTypes, close, submit } = props;
  const [formValues, setFormValues] = useState<IntegrationFormData>(emptyFormValues);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [showPayloadExample, setShowPayloadExample] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value });
  };

  const validate = (values: IntegrationFormData) => {
    try {
      addIntegrationValidationSchema.validateSync(values, { abortEarly: false });
      setErrors({});
      return true;
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const newErrors = err.inner?.reduce((acc, curr) => {
          if (curr.path) {
            acc[curr.path] = curr?.message;
          }
          return acc;
        }, {} as ValidationErrors);
        setErrors(newErrors);
      }
      return false;
    }
  };

  const handleSubmit = async () => {
    if (!validate(formValues)) {
      return;
    }

    const result = await submit(formValues);
    if (result?.validationError) {
      setErrors({ validation: result.validationError });
    }
  };

  const handleClose = () => {
    setFormValues(emptyFormValues);
    setErrors({});
    setShowPayloadExample(false);
    close();
  };

  useEffect(() => {
    if (initFormData) {
      setFormValues(initFormData);
      setErrors({});
    }
  }, [initFormData]);

  if (!hookEventTypes.length) {
    return null;
  }

  const isBtnDisabled = !formValues.name || !formValues.eventType || !formValues.url;

  return (
    <BhiFormDialog
      onClose={handleClose}
      open={isOpen}
      title={initFormData?.id ? 'Edit Webhook' : 'Add Webhook'}
      error={errors.validation}
      actions={
        <>
          <Button variant="outlined" onClick={handleClose}>Cancel</Button>
          <Button variant="contained" disabled={isBtnDisabled} onClick={handleSubmit}>
            {initFormData?.id ? 'Update' : 'Add Webhook'}
          </Button>
        </>
      }
    >
      <TextInput
        label="Name"
        name="name"
        margin="none"
        size="small"
        value={formValues.name}
        onChange={handleChange}
        error={!!errors.name}
        helperText={errors.name}
      />
      <Dropdown
        label="Event"
        margin="none"
        size="small"
        value={formValues.eventType}
        options={hookEventTypes.map(event => ({ value: event.event_type, title: event.description }))}
        onChange={(e, eventType) => setFormValues({ ...formValues, eventType })}
        error={!!errors.eventType}
        helperText={errors.eventType}
      />
      <TextInput
        label="Webhook URL"
        name="url"
        margin="none"
        size="small"
        value={formValues.url}
        onChange={handleChange}
        error={!!errors.url}
        helperText={errors.url}
      />
      <Button
        sx={{ alignSelf: 'flex-start' }}
        variant="text"
        size="small"
        endIcon={<HelpIcon />}
        aria-expanded={showPayloadExample}
        onClick={() => setShowPayloadExample(!showPayloadExample)}
      >
        {showPayloadExample ? 'Hide Payload Example' : 'Show Payload Example'}
      </Button>
      {showPayloadExample && (
        <CodeBlock>
          {hookEventTypes.find(event => event.event_type === formValues.eventType)?.payloadExample || ''}
        </CodeBlock>
      )}
    </BhiFormDialog>
  );
};
